import type { PlanSummary } from '@developerz/domain';
import { IconLayers } from '@developerz/ui';
import { createMemo, For, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { planCompletion, shortPlanId } from '../lib/custom-plans';
import { absolute, ago } from '../lib/time';
import { Avatar } from './Avatar';
import { ProgressRing } from './ProgressRing';
import { StatusPill } from './StatusPill';

// One "Custom plans" row (#4341) — a real `<button>` (`styles/views/_fleet.scss`
// `.custom-plan-row`: "reset the button chrome down to the card surface"), so
// Up/Down/Enter in `CustomPlansSection.tsx`'s keydown handler move and toggle
// REAL DOM focus/activation rather than a virtual cursor.
//
// NO FOCUSABLE DESCENDANT lives inside this button — no nested `<a>`, no copy
// button. `expectAxeClean` (`testing/axe.ts`) only disables `color-contrast`
// and `region`; `nested-interactive` stays live, and a `<button>`/`<a>` inside
// `<button class="custom-plan-row">` is invalid content model on top of that.
// Re-read against the ISSUE's own bullets (not the paraphrase that first
// listed "Open full view" here): that link, and the copy control, are under
// its "Breakdown:" section, not "Row:" — so this presenter shows the full id
// via `title=` only, which needs no click target, and leaves the copy
// affordance and the "Open full view" link to `PlanBreakdown.tsx`, which
// carries both beside each other in its actions row.
//
// A CANCELLED plan (`planCompletion` -> `{kind:'cancelled'}`) gets a muted,
// static ring — never `<ProgressRing/>`, which always paints its own `N%` text
// with no way to suppress it from outside. Reusing `.progress-ring`/
// `.progress-ring__track` (both already in `styles/ui/_progress.scss`) keeps
// the muted ring visually identical to the live one's empty track.
//
// OWNER LABEL (#4477): `<Avatar/>` stays `aria-hidden` and decorative — the
// adjacent `owner.login` text is what carries the accessible name, exactly
// its own contract — so this adds no focusable descendant to the button
// above. `owner: null` is the common case — measured on prod 2026-09-22
// (#4699): 0 of 1,458 plans carried a bound owner, because nearly every
// plan that read `initiated_by='user'` under the old token-tier rule was an
// agent PAT with no bound user, never a person — and renders a named,
// titled "Unknown owner" state rather than a blank gap or a guess — an
// absent fact stays in the muted italic voice
// (`.custom-plan-row__owner-unknown`), same idiom as
// `.repo-scorecard__evidence--none`.

export type CustomPlanRowProps = {
  readonly plan: PlanSummary;
  readonly expanded: boolean;
  readonly onToggle: () => void;
};

const RING_SIZE = 40;
const RING_STROKE = 4;

function MutedRing(): JSX.Element {
  const radius = (RING_SIZE - RING_STROKE) / 2;
  const center = RING_SIZE / 2;
  return (
    <svg
      class="progress-ring"
      aria-hidden="true"
      width={RING_SIZE}
      height={RING_SIZE}
      viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}
    >
      <circle
        class="progress-ring__track"
        cx={center}
        cy={center}
        r={radius}
        stroke-width={RING_STROKE}
        fill="none"
      />
    </svg>
  );
}

export function CustomPlanRow(props: CustomPlanRowProps): JSX.Element {
  const completion = createMemo(() => planCompletion(props.plan));
  const repos = (): readonly string[] => [
    props.plan.repoFullName,
    ...props.plan.repoFullNames.filter((repo) => repo !== props.plan.repoFullName),
  ];

  return (
    <button
      type="button"
      class="custom-plan-row"
      data-plan-id={props.plan.planId}
      aria-expanded={props.expanded ? 'true' : 'false'}
      onClick={() => props.onToggle()}
    >
      <div class="custom-plan-row__lead">
        <IconLayers size="1rem" />
        <span class="custom-plan-row__title">{props.plan.leadTitle}</span>
        <span class="custom-plan-row__ring">
          <Show
            when={completion().kind === 'progress'}
            fallback={
              <>
                <MutedRing />
                <span class="sr-only">{t('fleet.plans.custom.cancelled_pill')}</span>
              </>
            }
          >
            <ProgressRing
              pct={(() => {
                const c = completion();
                return c.kind === 'progress' ? c.pct : 0;
              })()}
              label={t('fleet.plans.custom.ring_label', {
                landed: String(completion().landed),
                total: String(completion().total),
              })}
            />
          </Show>
        </span>
      </div>

      <div class="custom-plan-row__meta">
        <span class="custom-plan-row__owner">
          <Show
            when={props.plan.owner}
            fallback={
              <span
                class="custom-plan-row__owner-unknown"
                title={t('fleet.plans.custom.owner_unknown_title')}
              >
                {t('fleet.plans.custom.owner_unknown')}
              </span>
            }
          >
            {(owner) => (
              <>
                <Avatar
                  login={owner().login}
                  avatar={owner().avatar}
                  class="custom-plan-row__owner-avatar"
                />
                <span class="custom-plan-row__owner-login">{owner().login}</span>
              </>
            )}
          </Show>
        </span>

        <For each={repos()}>{(repo) => <span class="plan-row__repo">{repo}</span>}</For>

        <Show
          when={completion().kind === 'progress'}
          fallback={
            <StatusPill tone="neutral">{t('fleet.plans.custom.cancelled_pill')}</StatusPill>
          }
        >
          <span>
            {t('fleet.plans.custom.landed', {
              landed: String(completion().landed),
              total: String(completion().total),
            })}
          </span>
        </Show>

        <Show when={props.plan.stalledCount > 0}>
          <StatusPill tone="danger">
            {t('fleet.plans.custom.stalled', { count: String(props.plan.stalledCount) })}
          </StatusPill>
        </Show>

        {/* WHY the plan stopped (#5102) — the plan-level `stopReason`, derived
            server-side by `planStopReason`. The label reuses the EXISTING
            per-reason copy: `held.<reason>` / `stall.<reason>`, both already
            enumerated per member by `i18n.test.ts`. The one `held` label that
            takes a parameter, `upstream_group_unmerged`, can never reach this
            pill — its rank is null, so it never wins the derivation. */}
        <Show when={props.plan.stopReason}>
          {(stop) => (
            <StatusPill tone="danger">
              {t('fleet.plans.custom.stop_reason_pill', {
                reason: t(
                  stop().kind === 'held'
                    ? `fleet.plans.custom.held.${stop().reason}`
                    : `fleet.plans.custom.stall.${stop().reason}`,
                ),
                count: String(stop().groupCount),
              })}
            </StatusPill>
          )}
        </Show>

        <span title={absolute(props.plan.updatedIso)}>
          {t('fleet.plans.custom.updated', { when: ago(props.plan.updatedIso) })}
        </span>

        <code class="custom-plan-row__id" title={props.plan.planId}>
          {shortPlanId(props.plan.planId)}
        </code>
      </div>
    </button>
  );
}
